import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useData } from "../../contexts/DataContext";
import { useAuth } from "../../contexts/AuthContext";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { LayoutDashboard, CalendarDays, Users, Plus, Layers, MessageSquare, ClipboardList, Presentation, FolderOpen, TrendingUp, X } from "lucide-react";
import SpaceTreeItem from "./SpaceTreeItem";
import EntityDialog, { SPACE_COLORS } from "./EntityDialog";
import { cn } from "../../lib/utils";

const NAV_ITEMS = [
  { to: "/", end: true, icon: LayoutDashboard, label: "Dashboard", testid: "nav-dashboard" },
  { to: "/tasks", icon: ClipboardList, label: "All Tasks", testid: "nav-all-tasks" },
  { to: "/chat", icon: MessageSquare, label: "Chat", testid: "nav-chat" },
  { to: "/whiteboards", icon: Presentation, label: "Whiteboards", testid: "nav-whiteboards" },
  { to: "/files", icon: FolderOpen, label: "Files", testid: "nav-files", hideFor: ["pelaksana"] },
  { to: "/omzet", icon: TrendingUp, label: "Omzet", testid: "nav-omzet", adminOnly: true },
  { to: "/calendar", icon: CalendarDays, label: "Calendar", testid: "nav-calendar" },
  { to: "/team", icon: Users, label: "Team", testid: "nav-team" },
];

function entityDialogTitle(dialog) {
  const entity = dialog.type === "space" ? "Space" : "Project";
  return dialog.mode === "create" ? `New ${entity}` : `Rename ${entity}`;
}

async function submitEntity(dialog, name, color, { upsertSpace, upsertProject }) {
  if (dialog.type === "space" && dialog.mode === "create") {
    const { data } = await api.post("/spaces", { name, color });
    upsertSpace(data);
    toast.success("Space created");
    return;
  }
  if (dialog.type === "space") {
    const { data } = await api.patch(`/spaces/${dialog.entity.id}`, { name, color });
    upsertSpace(data);
    toast.success("Space updated");
    return;
  }
  if (dialog.mode === "create") {
    const { data } = await api.post("/projects", { space_id: dialog.spaceId, name });
    upsertProject(data);
    toast.success("Project created");
    return;
  }
  const { data } = await api.patch(`/projects/${dialog.entity.id}`, { name });
  upsertProject(data);
  toast.success("Project updated");
}

function NavLinks({ collapsed }) {
  const { user } = useAuth();
  const navCls = ({ isActive }) =>
    cn("flex items-center gap-2.5 px-3 h-8 rounded-md text-sm transition-colors",
      collapsed && "md:justify-center md:px-2 md:gap-0",
      isActive ? "bg-[#E31E24]/10 text-[#E31E24] font-semibold" : "text-muted-foreground hover:bg-muted hover:text-foreground");
  return NAV_ITEMS.filter((i) => (!i.adminOnly || user?.role === "admin") && !(i.hideFor || []).includes(user?.role)).map(({ to, end, icon: Icon, label, testid }) => (
    <NavLink key={to} to={to} end={end} className={navCls} data-testid={testid} title={label}>
      <Icon size={16} className="shrink-0" />
      <span className={cn(collapsed && "md:hidden")}>{label}</span>
    </NavLink>
  ));
}

async function deleteSpaceEntity(space, deps) {
  if (!window.confirm(`Delete space "${space.name}" and all its projects/tasks?`)) return;
  try {
    await api.delete(`/spaces/${space.id}`);
    deps.removeSpace(space.id);
    deps.projects.filter((p) => p.space_id === space.id).forEach((p) => deps.removeProject(p.id));
    deps.refreshTasks();
    toast.success("Space deleted");
    deps.navigate("/");
  } catch (e) { toast.error(formatApiError(e)); }
}

async function deleteProjectEntity(project, deps) {
  if (!window.confirm(`Delete project "${project.name}" and all its tasks?`)) return;
  try {
    await api.delete(`/projects/${project.id}`);
    deps.removeProject(project.id);
    deps.refreshTasks();
    toast.success("Project deleted");
    deps.navigate("/");
  } catch (e) { toast.error(formatApiError(e)); }
}

function SpacesSection({ collapsed, spaces, projects, treeCollapsed, onToggle, onDialog, onDeleteSpace, onDeleteProject }) {
  return (
    <div className={cn("pt-4", collapsed && "md:hidden")}>
      <div className="flex items-center justify-between px-3 mb-1">
        <span className="text-[10px] font-semibold tracking-[0.05em] uppercase text-muted-foreground">Spaces</span>
        <button data-testid="add-space-button" onClick={() => onDialog({ type: "space", mode: "create" })}
          className="h-5 w-5 flex items-center justify-center rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors">
          <Plus size={14} />
        </button>
      </div>

      {spaces.map((space) => (
        <SpaceTreeItem key={space.id} space={space} projects={projects}
          isOpen={!treeCollapsed[space.id]} onToggle={onToggle} onDialog={onDialog}
          onDeleteSpace={onDeleteSpace} onDeleteProject={onDeleteProject} />
      ))}
      {spaces.length === 0 && (
        <button data-testid="add-space-empty" onClick={() => onDialog({ type: "space", mode: "create" })}
          className="flex items-center gap-2 px-3 h-8 text-sm text-muted-foreground hover:text-[#E31E24] w-full rounded-md hover:bg-muted">
          <Layers size={14} />Create your first space
        </button>
      )}
    </div>
  );
}

export default function Sidebar({ mobileOpen = false, collapsed = false, onClose }) {
  const { spaces, projects, upsertSpace, upsertProject, removeSpace, removeProject, refreshTasks } = useData();
  const [treeCollapsed, setTreeCollapsed] = useState({});
  const [dialog, setDialog] = useState(null); // {type: 'space'|'project', mode:'create'|'edit', entity?, spaceId?}
  const navigate = useNavigate();

  const toggle = (id) => setTreeCollapsed((p) => ({ ...p, [id]: !p[id] }));

  const handleSubmit = (name, color) => submitEntity(dialog, name, color, { upsertSpace, upsertProject });
  const entityDeps = { projects, removeSpace, removeProject, refreshTasks, navigate };
  const deleteSpace = (space) => deleteSpaceEntity(space, entityDeps);
  const deleteProject = (project) => deleteProjectEntity(project, entityDeps);

  return (
    <aside
      data-testid="sidebar"
      className={cn(
        "w-[260px] flex-shrink-0 border-r border-border bg-surface flex flex-col overflow-hidden z-40",
        "fixed inset-y-0 left-0 transition-transform duration-200 md:static md:transition-[width]",
        collapsed ? "md:w-16" : "md:w-[260px]",
        mobileOpen ? "translate-x-0 shadow-2xl" : "-translate-x-full md:translate-x-0"
      )}>
      <div className={cn("h-14 flex items-center gap-2.5 px-4 border-b border-border shrink-0", collapsed && "md:justify-center md:px-2")}>
        <div className="bg-white rounded-md p-1 border border-border shrink-0">
          <img src="/logo.png" alt="BWN" className="h-7 w-auto" data-testid="sidebar-logo" />
        </div>
        <div className={cn("min-w-0", collapsed && "md:hidden")}>
          <p className="font-heading font-extrabold text-sm leading-tight truncate">BWN PROJECT</p>
          <p className="text-[10px] text-muted-foreground uppercase tracking-wider leading-tight">Workspace</p>
        </div>
        <button data-testid="sidebar-mobile-close" onClick={onClose}
          className="md:hidden ml-auto h-7 w-7 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
          <X size={16} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
        <NavLinks collapsed={collapsed} />
        <SpacesSection collapsed={collapsed} spaces={spaces} projects={projects} treeCollapsed={treeCollapsed}
          onToggle={toggle} onDialog={setDialog} onDeleteSpace={deleteSpace} onDeleteProject={deleteProject} />
      </div>

      {dialog && (
        <EntityDialog open onClose={() => setDialog(null)}
          title={entityDialogTitle(dialog)}
          testid={dialog.type === "space" ? "space-dialog" : "project-dialog"}
          showColor={dialog.type === "space"}
          initialName={dialog.entity?.name || ""}
          initialColor={dialog.entity?.color || SPACE_COLORS[0]}
          onSubmit={handleSubmit} />
      )}
    </aside>
  );
}
